import { createFileRoute, Link } from "@tanstack/react-router";
import { ShoppingBag, ArrowRight } from "lucide-react";
import { useStoreCatalog, type StoreProduct } from "@/knowmeno/store/storeQueries";
import { AffiliateDisclosure } from "@/knowmeno/store/AffiliateDisclosure";

export const Route = createFileRoute("/store")({
  head: () => ({
    meta: [
      { title: "KnowMeno Store — Wearables, cooling sheets & menopause aids" },
      {
        name: "description",
        content:
          "Hand-picked wearables, cooling bedding, pelvic-floor and symptom-relief products for women in perimenopause and menopause.",
      },
      { property: "og:title", content: "KnowMeno Store — Menopause-friendly picks" },
      {
        property: "og:description",
        content:
          "Sleep cooler, track smarter, feel better. Curated wearables and menopause aids.",
      },
      { property: "og:url", content: "https://knowmeno.com/store" },
      { name: "robots", content: "index, follow" },
    ],
    links: [{ rel: "canonical", href: "https://knowmeno.com/store" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "KnowMeno Store",
          description:
            "Curated wearables, cooling bedding, pelvic-floor tools and symptom-relief products for perimenopause and menopause.",
          url: "https://knowmeno.com/store",
          isPartOf: {
            "@type": "WebSite",
            name: "KnowMeno",
            url: "https://knowmeno.com",
          },
          about: { "@type": "Thing", name: "Menopause and perimenopause products" },
        }),
      },
    ],
  }),
  notFoundComponent: () => <div className="p-8">Not found.</div>,
  component: StorePage,
});

function StorePage() {
  const { categories, products, error, loading } = useStoreCatalog();

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <Link to="/" aria-label="Back to KnowMeno home" className="text-sm text-muted-foreground hover:text-foreground">
            ← Back to KnowMeno
          </Link>
          <div className="flex items-center gap-2 font-serif text-xl">
            <ShoppingBag className="h-5 w-5" /> Store
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-4 pt-8 pb-4">
        <h1 className="font-serif text-3xl sm:text-4xl">Menopause Essentials & Wearables Store</h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Wearables to track your cycle and sleep, cooling sheets to survive night
          sweats, and small comforts that make midlife a little easier.
        </p>
        <div className="mt-4">
          <AffiliateDisclosure />
        </div>
      </section>

      {error && (
        <div className="mx-auto max-w-6xl px-4 py-8 text-center text-sm text-destructive">
          Couldn't load the catalog. Please refresh.
        </div>
      )}

      {loading && (
        <div className="mx-auto max-w-6xl px-4 py-12 text-center text-muted-foreground">
          Loading store…
        </div>
      )}

      {categories && products && (
        <>
          <nav className="mx-auto max-w-6xl px-4 pb-2">
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <a
                  key={c.id}
                  href={`#cat-${c.slug}`}
                  className="rounded-full border bg-card px-3 py-1 text-sm hover:bg-muted"
                >
                  {c.name}
                </a>
              ))}
            </div>
          </nav>

          <main className="mx-auto max-w-6xl space-y-12 px-4 py-8">
            {categories.map((cat) => {
              const items = products.filter((p) => p.category_id === cat.id);
              if (items.length === 0) return null;
              return (
                <section key={cat.id} id={`cat-${cat.slug}`}>
                  <div className="mb-4">
                    <h2 className="font-serif text-2xl">{cat.name}</h2>
                    {cat.description && (
                      <p className="mt-1 text-sm text-muted-foreground">
                        {cat.description}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {items.map((p) => (
                      <ProductCard key={p.id} product={p} />
                    ))}
                  </div>
                </section>
              );
            })}

            <p className="border-t pt-6 text-center text-xs text-muted-foreground">
              KnowMeno does not provide medical advice. Always talk to your clinician
              before starting new supplements, devices, or treatments.
            </p>
          </main>
        </>
      )}
    </div>
  );
}

function ProductCard({ product }: { product: StoreProduct }) {
  return (
    <Link
      to="/store/$slug"
      params={{ slug: product.slug }}
      className="group flex flex-col overflow-hidden rounded-lg border bg-card transition hover:shadow-md"
    >
      <div className="aspect-[4/3] w-full bg-muted">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.title}
            width={800}
            height={600}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-3xl text-muted-foreground/70">
            <ShoppingBag className="h-10 w-10" />
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        {product.brand && (
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            {product.brand}
          </span>
        )}
        <h3 className="font-semibold leading-snug">{product.title}</h3>
        <p className="text-sm text-muted-foreground">{product.short_desc}</p>
        <div className="mt-auto flex items-center justify-between pt-2">
          {product.price_display && (
            <span className="text-sm font-medium">{product.price_display}</span>
          )}
          <span className="inline-flex items-center gap-1 text-sm text-primary group-hover:underline">
            Details <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}
